import React, { useEffect, useState } from 'react';
import { X, ChevronDown, ChevronUp } from 'lucide-react';
import { ArrivalsIcon } from '../../../components/icons';
import { ARRIVALS_WIDTH } from './MapArrivalsStrip';
import { floatingSurfaceClass } from '../floatingSurfaceStyles';

const SURFACE = floatingSurfaceClass(97);

const UNITS = ['boxes', 'kg', 'lbs'];

/** Tab figures are compact ("1.86M"); the summary and footer are exact. */
const compactFmt = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 2 });
const compact = (n) => compactFmt.format(Math.round(Number(n) || 0));
const full = (n) => Math.round(Number(n) || 0).toLocaleString();

const dayLabel = (ms) => new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
const weekday = (ms) => new Date(ms).toLocaleDateString(undefined, { weekday: 'short' });

/**
 * One window tab: its length, what is arriving in it, and across how many
 * shipments. The figures sit in the tab on purpose — the point of three tabs
 * is comparing the windows, which you cannot do if only the open one has
 * numbers.
 */
const WindowTab = ({ days, total, countLabel, unitLabel, active, onSelect, label }) => (
  <button
    type="button"
    onClick={() => onSelect(days)}
    aria-pressed={active}
    className={`flex min-w-0 flex-1 cursor-pointer flex-col items-center gap-[3px] border-b-2 border-solid px-[4px] pb-[8px] pt-[9px] transition-colors ${
      active ? 'border-[#16A34A]' : 'border-transparent hover:bg-[#F7F9F7] dark:hover:bg-[#20251F]'
    }`}
  >
    <span className={`font-['Inter'] text-[12.5px] whitespace-nowrap ${
      active ? 'font-extrabold text-[#15803D] dark:text-[#7EE6A4]' : 'font-bold text-[#6B7280] dark:text-[#9AA09A]'
    }`}>
      {label}
    </span>
    <span className={`font-['Inter'] text-[11px] font-bold whitespace-nowrap ${
      active ? 'text-[#15803D] dark:text-[#7EE6A4]' : 'text-[#9CA3AF] dark:text-[#6B716C]'
    }`}>
      {compact(total)}
    </span>
    <span className={`font-['Inter'] text-[10.5px] font-semibold whitespace-nowrap ${
      active ? 'text-[#15803D] dark:text-[#7EE6A4]' : 'text-[#9CA3AF] dark:text-[#6B716C]'
    }`}>
      {countLabel}
    </span>
    <span className="sr-only">{unitLabel}</span>
  </button>
);

/**
 * The Arrivals pop-up — the arrivals pill's open state.
 *
 * Reads top to bottom as one question narrowing: which window, how much in
 * it, then which days make it up. The three windows carry their own figures
 * so the choice can be made before committing to it.
 *
 * Presentational: every figure arrives formatted or countable, and the caller
 * owns the window, the unit and what is in each day.
 *
 * @param {Array<{days: number, total: number, count: number}>} windows  Tab figures.
 * @param {Array<{dayMs: number, total: number, rows: object[]}>} groups  Body, in calendar order.
 */
const MapAllWidget = ({
  title,
  windows = [],
  groups = [],
  days = 7,
  onDaysChange,
  total = 0,
  count = 0,
  unit = 'boxes',
  onUnitChange,
  unitLabels = {},
  labels = {},
  onClose,
  className = '',
}) => {
  // The first day that has anything, so the panel opens showing arrivals
  // rather than a column of collapsed headers.
  const firstFilled = groups.find((g) => g.rows.length > 0)?.dayMs ?? null;
  const [openDay, setOpenDay] = useState(firstFilled);
  // Switching window rebuilds the list, and the day that was open may not be
  // in the new one — leaving every day collapsed with no sign why.
  useEffect(() => { setOpenDay(firstFilled); }, [days, firstFilled]);
  const unitLabel = unitLabels[unit] || unit;

  return (
    // Same width as the Arrivals pill this hangs off — a narrower panel read
    // as a layout bug rather than a dropdown.
    // No fixed height: the caller stretches this to the bottom of the map and
    // bounds it there, so a ten-day window uses the screen it has instead of
    // scrolling inside the mock's 398px. `min-h-0` is what lets the body
    // below scroll rather than pushing the footer out of the panel.
    <div className={`${ARRIVALS_WIDTH} flex min-h-0 flex-col overflow-hidden rounded-[14px] border border-solid ${SURFACE} ${className}`}>
      <div className="flex h-[49px] shrink-0 items-center gap-[6px] border-b border-solid border-[#EEF0EC] px-[12px] dark:border-[#2A302C]">
        {/* The box, not the layers glyph: this panel is the Arrivals strip's
            open state, and the design heads it with the strip's own icon. */}
        <ArrivalsIcon className="h-[16px] w-[16px] shrink-0 text-[#16A34A] dark:text-[#22C55E]" />
        <span className="ml-[2px] truncate font-['Inter'] text-[13.5px] font-extrabold tracking-[-0.135px] text-[#111827] dark:text-[#E6E8E6]">
          {title}
        </span>
        <span className="ml-auto flex h-[22px] shrink-0 items-center rounded-[7px] bg-[#F1F3F0] p-[2px] dark:bg-[#252B27]">
          {UNITS.map((u) => (
            <button
              key={u}
              type="button"
              onClick={() => onUnitChange?.(u)}
              className={`flex h-[18px] cursor-pointer items-center rounded-[5px] px-[7px] font-['Inter'] text-[10px] font-bold transition-colors ${
                u === unit
                  ? 'bg-white text-[#15803D] drop-shadow-[0px_1px_1px_#1118271A] dark:bg-[#1B1F1C] dark:text-[#22C55E]'
                  : 'text-[#6B7280] dark:text-[#9AA09A]'
              }`}
            >
              {unitLabels[u] || u}
            </button>
          ))}
        </span>
        {/* Closing from the panel itself. The pill above still toggles it, but
            with the panel open the pill is the thing the panel is covering. */}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label={labels.close || 'Close'}
            className="flex size-[26px] shrink-0 cursor-pointer items-center justify-center rounded-full bg-[#F3F5F2] text-[#5D625F] transition-colors hover:bg-[#E8EDEA] dark:bg-[#252B27] dark:text-[#9AA09A]"
          >
            <X size={12} />
          </button>
        )}
      </div>

      <div className="flex shrink-0 items-stretch border-b border-solid border-[#EEF0EC] px-[6px] dark:border-[#2A302C]">
        {windows.map((w) => (
          <WindowTab
            key={w.days}
            days={w.days}
            total={w.total}
            label={labels.window?.(w.days) ?? `${w.days} days`}
            countLabel={labels.shipments?.(w.count) ?? `${w.count} shipments`}
            unitLabel={unitLabel}
            active={w.days === days}
            onSelect={onDaysChange}
          />
        ))}
      </div>

      <div className="flex h-[59px] shrink-0 items-center gap-[9px] border-b border-solid border-[#EEF0EC] px-[12px] dark:border-[#2A302C]">
        <span className="font-['Inter'] text-[21px] font-extrabold tracking-[-0.42px] text-[#111827] dark:text-[#E6E8E6]">
          {full(total)}
        </span>
        <span className="min-w-0 font-['Inter'] text-[12px] font-semibold text-[#6B7280] dark:text-[#9AA09A]">
          {unitLabel}
        </span>
        <span className="ml-auto flex h-[36px] shrink-0 items-center rounded-[12px] bg-[#F3F5F2] px-[9px] text-center font-['Inter'] text-[11.5px] font-bold leading-[14px] text-[#4B5563] dark:bg-[#252B27] dark:text-[#9AA09A]">
          {labels.shipmentsStacked?.(count) ?? `${count} shipments`}
        </span>
      </div>

      {/* The only part that scrolls: the header, tabs, summary and total stay
          put so the figure you are reading cannot slide out from under you. */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {groups.map((g) => {
          const isOpen = openDay === g.dayMs;
          const empty = g.rows.length === 0;
          return (
            <div key={g.dayMs}>
              <button
                type="button"
                disabled={empty}
                onClick={() => setOpenDay(isOpen ? null : g.dayMs)}
                aria-expanded={isOpen}
                className={`flex h-[35px] w-full items-center gap-[8px] border-y border-solid border-[#EEF0EC] bg-[#F5F6F4] px-[12px] dark:border-[#2A302C] dark:bg-[#20251F] ${
                  empty ? 'cursor-default' : 'cursor-pointer'
                }`}
              >
                <span className="flex size-[12px] shrink-0 items-center justify-center text-[#5D625F] dark:text-[#9AA09A]">
                  {empty ? null : isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                </span>
                <span className={`font-['Inter'] text-[12.5px] font-extrabold ${
                  empty ? 'text-[#6B7280] dark:text-[#6B716C]' : 'text-[#111827] dark:text-[#E6E8E6]'
                }`}>
                  {dayLabel(g.dayMs)}
                </span>
                <span className="font-['Inter'] text-[12.5px] font-medium text-[#9CA3AF] dark:text-[#6B716C]">
                  {weekday(g.dayMs)}
                </span>
                <span className={`ml-auto font-['Inter'] text-[12.5px] font-extrabold ${
                  empty ? 'text-[#9CA3AF] dark:text-[#6B716C]' : 'text-[#15803D] dark:text-[#7EE6A4]'
                }`}>
                  {full(g.total)}
                </span>
                <span className={`font-['Inter'] text-[12.5px] font-semibold ${
                  empty ? 'text-[#9CA3AF] dark:text-[#6B716C]' : 'text-[#15803D] dark:text-[#7EE6A4]'
                }`}>
                  {unitLabel}
                </span>
              </button>

              {isOpen && !empty && (
                <div className="flex flex-col">
                  {g.rows.map((row) => (
                    // `items-start`: the label wraps to several lines rather than
                    // clipping to an ellipsis, and the value belongs against its
                    // first line instead of the middle of the block.
                    <div key={row.key} className="flex items-start gap-[8px] py-[7px] pl-[32px] pr-[12px]">
                      <span className="min-w-0 flex-1 break-words font-['Inter'] text-[12px] font-medium text-[#6B7280] dark:text-[#9AA09A]">
                        {row.label}
                        {/* How many shipments this variety arrived on — the
                            design's "Hass (2)". Omitted at one, where it would
                            just be noise on every line. */}
                        {row.count > 1 ? (
                          <span className="text-[#9CA3AF] dark:text-[#6B716C]">{` (${row.count})`}</span>
                        ) : null}
                      </span>
                      <span className="shrink-0 font-['Inter'] text-[12.5px] font-bold text-[#111827] dark:text-[#E6E8E6]">
                        {full(row.value)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex h-[42px] shrink-0 items-center gap-[8px] border-t border-solid border-[#E6E9E4] bg-[#FBFCFB] px-[12px] dark:border-[#2A302C] dark:bg-[#171B18]">
        <span className="font-['Inter'] text-[10.5px] font-extrabold uppercase tracking-[0.63px] text-[#9CA3AF] dark:text-[#6B716C]">
          {labels.total?.(days) ?? `Total · ${days} days`}
        </span>
        <span className="ml-auto font-['Inter'] text-[15px] font-extrabold text-[#111827] dark:text-[#E6E8E6]">
          {full(total)}
        </span>
        <span className="font-['Inter'] text-[12px] font-semibold text-[#6B7280] dark:text-[#9AA09A]">
          {unitLabel}
        </span>
      </div>
    </div>
  );
};

export default MapAllWidget;
